import { useState, useRef, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import { createPortal } from "react-dom";

const ColumnSelection = ({ visibleColumns, setVisibleColumns, columnOrder, columnDefinitions: customColumnDefinitions }) => {
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const isDarkMode = useDarkMode();
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  // Default column definitions with proper labels (for orders)
  const defaultColumnDefinitions = {
    frontorderid: "Front Order ID",
    orderType: "Order Type", 
    orderStatus: "Order Status",
    orderSendBy: "Order Send By",
    invoiceNo: "Invoice No",
    source: "Source",
    tillname: "Till Name",
    customer: "Customer",
    orderDate: "Order Date",
    paymentStatus: "Payment Status",
    paymentMethod: "Payment Method",
    totalAmount: "Total Amount",
    fillBy: "Fill By",
    // Sales by Products columns
    productSKU: "SKU",
    quantitySold: "Qty Sold",
    grossSales: "Gross Sales",
    discountsPromotions: "Discounts",
    netSalesIncTax: "Net Sales Inc. Tax",
    netSalesExcTax: "Net Sales Exc. Tax",
    taxAmount: "Tax Amount",
    costOfGoods: "Cost of Goods",
    grossProfit: "Gross Profit",
    grossProfitMargin: "Gross Profit Margin %",
    transactionQty: "Transaction Qty",
    refundQty: "Refund Qty",
    refundValue: "Refund Value",
    // Sales by Time columns
    hourCommencing: "Hour Commencing",
    itemQty: "Item Qty",
    salesIncTax: "Sales Inc. Tax",
    avgTransactionNetSales: "Avg Transaction Net Sales"
  };

  // Use custom definitions if provided, otherwise use defaults
  const columnDefinitions = customColumnDefinitions || defaultColumnDefinitions;


  const handleColumnSelectionButton = () => {
    setShowColumnPanel((prev) => !prev);
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column],
    });
  };

  const selectAllColumns = () => {
    const allSelected = {};
    Object.keys(visibleColumns).forEach(column => {
      allSelected[column] = true;
    });
    setVisibleColumns(allSelected);
  };

  const deselectAllColumns = () => {
    const noneSelected = {};
    Object.keys(visibleColumns).forEach(column => {
      noneSelected[column] = false;
    });
    setVisibleColumns(noneSelected);
  };

  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        panelRef.current && 
        !panelRef.current.contains(event.target) && 
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setShowColumnPanel(false);
      }
    };

    if (showColumnPanel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnPanel]);

  // Force z-index on column panel when it's shown
  useEffect(() => {
    if (showColumnPanel && panelRef.current) {
      const panel = panelRef.current;
      panel.style.zIndex = '2147483647';
      panel.style.position = 'absolute';
      panel.style.willChange = 'transform';
      
      // Force re-render to ensure styles are applied
      const timeout = setTimeout(() => {
        if (panel) {
          panel.style.zIndex = '2147483647';
          panel.style.position = 'absolute';
        }
      }, 10);
      
      return () => clearTimeout(timeout);
    }
  }, [showColumnPanel]);

  // Calculate button position for portal
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, [showColumnPanel]);

  // Update position on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (showColumnPanel && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonPosition({
          top: rect.bottom + window.scrollY + 4,
          right: window.innerWidth - rect.right
        });
      }
    };

    if (showColumnPanel) {
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showColumnPanel]);

  return (
    <div className="position-relative d-flex align-items-center">
      <button
        onClick={handleColumnSelectionButton}
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm d-flex align-items-center"
        style={{ 
          height: "32px",
          fontSize: "12px",
          border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
          borderRadius: "8px",
          backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
          color: isDarkMode ? "#ffffff" : "#495057",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 16px"
        }}
        title="Choose Header Columns"
      >
        <i className="fa fa-plus me-1"></i>
         
      </button>

      {showColumnPanel && createPortal(
        <div
          ref={panelRef}
          className="column-panel column-selection"
          style={{
            position: "fixed",
            top: `${buttonPosition.top}px`,
            right: `${buttonPosition.right}px`,
            backgroundColor: isDarkMode ? "#2d3436" : "white",
            border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
            borderRadius: "6px",
            padding: "12px",
            width: "200px",
            zIndex: 2147483647,
            boxShadow: isDarkMode ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)"
          }}
        >
          {/* Header */}
          <div 
            className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
            style={{ borderBottomColor: isDarkMode ? "#404040" : "#dee2e6" }}
          >
            <h6 
              className="mb-0 fw-semibold" 
              style={{ 
                fontSize: "13px", 
                color: isDarkMode ? "#ffffff" : "#000000" 
              }}
            >
              Select Columns
            </h6>
            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={selectAllColumns}
                style={{ 
                  fontSize: "10px", 
                  padding: "2px 6px", 
                  color: isDarkMode ? "#ffffff" : "#000000",
                  backgroundColor: isDarkMode ? "#232B2B" : "#ffffff",
                  borderColor: isDarkMode ? "#404040" : "#dee2e6"
                }}
                title="Select All"
              >
                All
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={deselectAllColumns}
                style={{ 
                  fontSize: "10px", 
                  padding: "2px 6px", 
                  color: isDarkMode ? "#ffffff" : "#000000",
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  borderColor: isDarkMode ? "#404040" : "#dee2e6"
                }}
                title="Deselect All"
              >
                None
              </button>
            </div>
          </div>

          {/* Column List */}
          <div style={{ maxHeight: "250px", overflowY: "auto" }}>
            {(columnOrder || Object.keys(visibleColumns)).map((column) => (
              <div key={column} className="d-flex align-items-center py-1">
                <input
                  type="checkbox"
                  id={`column-${column}`}
                  checked={visibleColumns[column]}
                  onChange={() => toggleColumnVisibility(column)}
                  className="me-2"
                  style={{ 
                    transform: "scale(0.9)",
                    accentColor: isDarkMode ? "#232B2B" : "#007bff"
                  }}
                />
                <label 
                  htmlFor={`column-${column}`}
                  className="mb-0"
                  style={{ 
                    fontSize: "12px",
                    cursor: "pointer",
                    flex: 1,
                    lineHeight: "1.3",
                    color: isDarkMode ? "#ffffff" : "#000000"
                  }}
                >
                  {columnDefinitions[column] || 
                   column.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) ||
                   column.charAt(0).toUpperCase() + column.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnSelection;
